"use client"

import React, { useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import Image from "../images/Image"
import CurrentLocation from "./CurrentLocation"
import AddInputs from "../Add/AddInputs"
import type { RootState } from "../../store/index"
import {
  setCoordinates,
  setLocationInfo,
  setError,
  setIsLoading,
  setIsModalOpen,
  setShowFields,
  setLocalErrors,
} from "../../store/slices/geolocationSlice"
import useTranslation from "../CustomHooks/useTranslation"
import { inputFieldContent } from "../inputfields/InputFieldContent"

interface LocationInfo {
  display_name: string
  address: {
    city?: string
    state?: string
    country?: string
  }
}
interface geolocationProps {
  inputCordinates: { latitude: number | null; longitude: number | null; },
  setInputCordinates: React.Dispatch<React.SetStateAction<any>>;
}
export default function GeolocationComponent({ inputCordinates, setInputCordinates }: geolocationProps) {
  const dispatch = useDispatch()
  const { translate } = useTranslation(inputFieldContent)
  const { coordinates, locationInfo, error, isLoading, isModalOpen, showFields } = useSelector(
    (state: RootState) => state.geolocation,
  )

  // const [inputCordinates, setInputCordinates] = React.useState<{ latitude: number | null; longitude: number | null; }>({
  //   latitude: null,
  //   longitude: null,
  // })

  const inputs = {
    "": {
      "Location Name": {
        required: false,
        disabled: true,
        id: "locationName",
        type: "input",
        tabIndex: 0,
        
      },
      Latitude: {
        required: false,
        disabled: true,
        id: "latitude",
        type: "input",
        tabIndex: 1,
        testId:'latitude-value'
      },
      Longitude: {
        required: false,
        disabled: true,
        id: "longitude",
        type: "input",
        tabIndex: 2,
        testId:'longitude-value'
      },
    },
  }

  const getLocationInfo = useCallback(
    async (latitude: number, longitude: number) => {
      dispatch(setIsLoading(true))
      dispatch(setError(null))
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
        )

        if (!response.ok) {
          throw new Error("Failed to fetch location info")
        }

        const data: LocationInfo = await response.json()
        dispatch(setLocationInfo(data))
      } catch (err) {
        dispatch(setError("Failed to get location name. Please try again."))
      } finally {
        dispatch(setIsLoading(false))
      }
    },
    [dispatch],
  )

  function debounce<T extends (...args: any[]) => any>(func: T, wait: number): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout | null = null

    return (...args: Parameters<T>) => {
      if (timeout) clearTimeout(timeout)
      timeout = setTimeout(() => func(...args), wait)
    }
  }

  const debouncedGetLocationInfo = useCallback(
    debounce((lat: number, lon: number) => getLocationInfo(lat, lon), 500),
    [getLocationInfo],
  )

  React.useEffect(() => {
    if (coordinates.latitude !== null && coordinates.longitude !== null) {
      debouncedGetLocationInfo(coordinates.latitude, coordinates.longitude)
    }
  }, [coordinates, debouncedGetLocationInfo])

  const getCurrentLocation = () => {
    dispatch(setIsLoading(true))
    dispatch(setError(null))
    dispatch(setLocalErrors({latitude :'', longitude: ''}))

    if (!navigator.geolocation) {
      dispatch(setError("Geolocation is not supported by this browser."))
      dispatch(setIsLoading(false))
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newCoordinates = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }
        // dispatch(setCoordinates(newCoordinates))
        setInputCordinates(newCoordinates);
        dispatch(setIsLoading(false))
      },
      (err) => {
        dispatch(setError(`Error: ${err.message}`))
        dispatch(setIsLoading(false))
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      },
    )
  }

  const handleInputChange = (id: string, value: string) => {
    if (id === "latitude" || id === "longitude") {
      dispatch(setCoordinates({ ...coordinates, [id]: Number.parseFloat(value) || null }))
    }
  }

  const handleClose = () => {
    dispatch(setIsModalOpen(false))
  }

  const handleGetAltitude = () => {
    // Simulating altitude retrieval
    dispatch(setCoordinates(inputCordinates))
    dispatch(setLocalErrors({latitude :'', longitude: ''}))
    dispatch(setIsLoading(true))
    setTimeout(() => {
      dispatch(setShowFields(true))
      dispatch(setIsLoading(false))
    }, 1000)
  }

  return (
    <>
      <div className="py-[18px]">
        <div className=" mt-8 flex cursor-pointer w-fit" data-testid={'locate_your_position'} onClick={() => dispatch(setIsModalOpen(true))}>
          <Image src="map-pin" />
          <p className="ml-2.5 text-p_primary text-heading4">{translate('Locate your Position')}</p>
        </div>
      </div>

      {showFields && (
        <AddInputs
          inputs={inputs}
          totalInputCount={3}
          inputValues={{
            locationName: locationInfo
              ? `${locationInfo?.address?.city || ""}, ${locationInfo?.address?.country || locationInfo?.address?.state || ""}`.replace(
                /^,\s/,
                "",
              )
              : "",
            latitude: coordinates.latitude?.toString() || "",
            longitude: coordinates.longitude?.toString() || "",
          }}
          onInputChange={handleInputChange}
          mapping={true}
        />
      )}
      {isModalOpen && (
        <CurrentLocation
          handleClose={handleClose}
          getCurrentLocation={getCurrentLocation}
          handleGetAltitude={handleGetAltitude}
          inputCordinates={inputCordinates}
          setInputCordinates={setInputCordinates}
        />
      )}
    </>
  )
}

